<template>
  <v-card class="fill-height text-left d-flex flex-column dsfr" outlined>
    <v-card-title class="font-weight-bold">
      {{ study.title }}
    </v-card-title>
    <v-card-subtitle>
      {{ study.canteen.name }}
      <!-- Les deux cibles cohabitent dans le plan d'action : la carte dit laquelle, sinon le
           gestionnaire ne distingue plus ce qu'il adresse à son équipe de ce qu'il adresse aux
           convives. Le libellé est en clair, jamais une couleur seule. -->
      <span class="target-mention">· {{ targetLabel }}</span>
    </v-card-subtitle>

    <v-card-text class="py-2">
      <p class="mb-1">
        <strong>Durée :</strong>
        {{ study.study.durationInDay }} jours
      </p>
      <p class="mb-1">
        <strong>Description :</strong>
        {{ isConsumer ? study.study.descriptionConsumer : study.study.descriptionGeneral }}
      </p>
    </v-card-text>

    <v-spacer></v-spacer>

    <v-card-actions class="px-4 py-4">
      <v-btn text class="rounded" :to="`/actions/${study.id}/details`">
        Voir les instructions
        <v-icon small class="menu-arrow ml-2" aria-hidden="true">$arrow-right-line</v-icon>
      </v-btn>
    </v-card-actions>

    <v-card-actions class="px-4 py-4">
      <v-spacer></v-spacer>

      <!-- État courant : porté par un texte, jamais par la seule couleur -->
      <v-chip v-if="stateLabel" :color="stateColor" label small class="font-weight-medium">
        {{ stateLabel }}
      </v-chip>

      <!-- Action possible : composée des deux axes (droit de pilotage + état de l'activité) -->
      <!-- Publier vient avant démarrer : tant que l'activité est en brouillon, elle n'est
           proposée à aucun convive et les enrôler ne leur montrerait rien. -->
      <v-btn v-else-if="canManage && isDraft" color="primary" variant="tonal" class="rounded" @click="publishAction()">
        Proposer aux convives
      </v-btn>
      <v-btn
        v-else-if="canManage && currentStatus === 'CREATED'"
        color="success"
        variant="tonal"
        class="rounded"
        @click="startAction()"
      >
        Démarrer l'action
      </v-btn>
      <v-btn
        v-else-if="canParticipate && !isParticipating"
        color="success"
        variant="tonal"
        class="rounded"
        @click="engageAction()"
      >
        Participer
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
export default {
  name: "ActionCard",
  props: {
    study: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      // Copies locales : on ne mute jamais les props
      currentStatus: this.study.status,
      // L'engagement est désormais un état lu sur la réponse (`isEngaged`) et non déduit de la
      // liste des inscrits : cette liste était l'identifiant de tous les convives inscrits,
      // renvoyée à chacun d'eux.
      engaged: !!this.study.isEngaged,
      // L'état de publication suit les gestes faits sur la carte, comme le statut.
      currentPublication: this.study.publication,
    }
  },
  computed: {
    // Rôles effectifs (rattachements CanteenMembership + job non-pivot), comme le routeur
    // et non le seul `job` primaire : un utilisateur multi-rôles est jugé sur ses rattachements.
    userRoles() {
      return this.$store.getters.userRoles
    },
    isConsumer() {
      return this.userRoles.includes("CONSUMER")
    },
    targetLabel() {
      return this.study.target === "CLIENT" ? "pour les convives" : "pour l'équipe de restauration"
    },
    // Le droit de piloter est **dit par le serveur** (`canManage`), et non déduit d'une liste de
    // professions : cette liste omettait « MANAGER », si bien que le gestionnaire se voyait
    // proposer « Participer » — un geste que l'API lui refuse. Le critère est le rattachement à
    // la cantine de l'activité, jamais la profession déclarée.
    canManage() {
      return !!this.study.canManage
    },
    canParticipate() {
      return !this.canManage
    },
    isDraft() {
      return this.currentPublication === "DRAFT"
    },
    isParticipating() {
      return this.engaged
    },
    // Un seul point de décision de l'état affiché, en composant les deux axes
    stateLabel() {
      if (this.canManage) {
        if (this.currentPublication === "ARCHIVED") return "Archivée"
        if (this.currentStatus === "ONGOING") return "Action en cours"
        if (this.currentStatus === "ENDED") return "Terminée"
        return null
      }
      return this.isParticipating ? "Vous participez" : null
    },
    stateColor() {
      if (this.currentPublication === "ARCHIVED") return "grey"
      if (this.currentStatus === "ENDED") return "grey"
      if (this.currentStatus === "ONGOING") return "warning"
      return "success"
    },
  },
  methods: {
    async startAction() {
      const studyId = this.study.id
      await this.$store
        .dispatch("startAction", { studyId })
        .then(() => {
          this.currentStatus = "ONGOING"
        })
        .catch((e) => this.$store.dispatch("notifyServerError", e))
    },
    async publishAction() {
      const studyId = this.study.id
      await this.$store
        .dispatch("publishAction", { studyId })
        .then(() => {
          this.currentPublication = "PUBLISHED"
        })
        .catch((e) => this.$store.dispatch("notifyServerError", e))
    },
    async engageAction() {
      const studyId = this.study.id
      await this.$store
        .dispatch("engageInAction", { studyId })
        .then(() => {
          this.engaged = true
        })
        .catch((e) => this.$store.dispatch("notifyServerError", e))
    },
  },
}
</script>

<style scoped>
.target-mention {
  font-style: italic;
}

.v-card-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
</style>
